import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { prefetchRoute, prefetchCoreRoutes } from '../App';
import { Button } from '../components/ui/button';
import EmailVerificationBanner from './EmailVerificationBanner';
import FirstRunCVModal from './FirstRunCVModal';
import { 
  Sparkles, 
  LayoutDashboard, 
  Briefcase, 
  FileText, 
  FolderOpen,
  LogOut,
  BarChart3,
  CreditCard,
  Menu,
  X,
  GraduationCap,
  Puzzle,
  Flame,
  Calendar,
  Moon,
  Sun,
  Coins,
  Users,
  TrendingUp
} from 'lucide-react';

const navItems = [
  { path: '/dashboard', label: 'Dashboard', icon: <LayoutDashboard className="w-5 h-5" /> },
  { path: '/jobs', label: 'Job Feed', icon: <Briefcase className="w-5 h-5" /> },
  { path: '/swipe', label: 'Swipe Mode', icon: <Flame className="w-5 h-5" /> },
  { path: '/applications', label: 'Applications', icon: <FolderOpen className="w-5 h-5" /> },
  { path: '/cv', label: 'My CVs', icon: <FileText className="w-5 h-5" /> },
  { path: '/interviews', label: 'Interviews', icon: <Calendar className="w-5 h-5" /> },
  { path: '/interview-prep', label: 'Interview Prep', icon: <GraduationCap className="w-5 h-5" /> },
  // { path: '/extension', label: 'Browser Extension', icon: <Puzzle className="w-5 h-5" /> }, // hidden in v1 — coming later
  { path: '/analytics', label: 'Analytics', icon: <BarChart3 className="w-5 h-5" /> },
  { path: '/founder/users', label: 'Users', icon: <Users className="w-5 h-5" />, founderOnly: true },
  { path: '/founder/funnel', label: 'Funnel', icon: <TrendingUp className="w-5 h-5" />, founderOnly: true },
  { path: '/subscription', label: 'Subscription', icon: <CreditCard className="w-5 h-5" /> },
];

const Sidebar = ({ isOpen, onClose }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    prefetchCoreRoutes();
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <>
      {isOpen && (
        <div className="fixed inset-0 bg-black/20 backdrop-blur-sm z-40 lg:hidden" onClick={onClose} />
      )}
      
      <aside className={`fixed left-0 top-0 h-screen w-64 bg-white dark:bg-[#111111] border-r border-zinc-100 dark:border-white/[0.08] z-50 transform transition-transform duration-300 lg:translate-x-0 ${isOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <div className="flex flex-col h-full">
          <div className="flex items-center justify-between p-4 border-b border-zinc-100 dark:border-white/[0.08] relative">
            <Link to="/" className="flex flex-col items-center w-full py-2">
              <img src="https://static.prod-images.emergentagent.com/jobs/4621c343-adba-42bf-84be-49e31a6afafb/images/40c8a552cca65f4dba8d2de4cceb45ed6fa5e8f8b68ecd07be2fab8cf5594a0e.png" alt="JobSurf AI" className="w-36 h-36 object-contain rounded-xl dark:brightness-110 dark:contrast-110" />
            </Link>
            <button onClick={onClose} className="lg:hidden text-zinc-500 dark:text-zinc-400 hover:text-zinc-700 dark:hover:text-white absolute top-6 right-6">
              <X className="w-5 h-5" />
            </button>
          </div>

          <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
            {navItems.filter(item => {
              if (item.founderOnly) return user?.is_founder;
              if (item.path === '/analytics') return user?.is_founder;
              return true;
            }).map((item) => {
              const isActive = location.pathname === item.path || location.pathname.startsWith(item.path + '/');
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={onClose}
                  onMouseEnter={() => prefetchRoute(item.path)}
                  className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-200 ${
                    isActive 
                      ? 'bg-indigo-50 dark:bg-white/[0.06] text-indigo-600 dark:text-white font-medium dark:shadow-[0_0_12px_rgba(255,255,255,0.04)]' 
                      : 'text-zinc-600 dark:text-zinc-500 hover:bg-zinc-50 dark:hover:bg-white/[0.04] hover:text-zinc-900 dark:hover:text-zinc-200'
                  }`}
                  data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
                >
                  {item.icon}
                  <span>{item.label}</span>
                </Link>
              );
            })}
          </nav>

          <div className="p-4 border-t border-zinc-100 dark:border-white/[0.08]">
            {/* Extra (top-up) tailorings bubble — only when the user actually has some */}
            {(user?.application_credits || 0) > 0 && (
            <Link
              to="/subscription"
              onClick={onClose}
              className="flex items-center gap-3 px-4 py-2.5 mb-2 rounded-xl bg-amber-50 dark:bg-amber-500/10 border border-amber-200 dark:border-amber-500/20 hover:bg-amber-100 dark:hover:bg-amber-500/15 transition-colors"
              data-testid="credits-bubble"
            >
              <Coins className="w-4 h-4 text-amber-600 dark:text-amber-400 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-xs font-semibold text-amber-800 dark:text-amber-300">
                  {user?.application_credits >= 999999 ? 'Unlimited' : (user?.application_credits || 0).toLocaleString()} Extra Tailorings
                </p>
              </div>
              {user?.is_founder && (
                <span className="text-[10px] font-bold bg-amber-500 text-white px-1.5 py-0.5 rounded-full">FOUNDER</span>
              )}
            </Link>
            )}

            <Link to="/profile" onClick={onClose} className="flex items-center gap-3 px-4 py-3 mb-2 rounded-xl hover:bg-zinc-50 dark:hover:bg-white/[0.04] transition-colors group" data-testid="edit-profile-link">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold ring-1 ring-transparent ${user?.is_founder ? 'bg-gradient-to-br from-amber-400 to-orange-500 text-white' : 'bg-indigo-100 dark:bg-white/[0.06] text-indigo-600 dark:text-zinc-300 dark:ring-white/[0.08]'}`}>
                {user?.name?.[0]?.toUpperCase() || 'U'}
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-zinc-900 dark:text-white truncate group-hover:text-indigo-600 dark:group-hover:text-zinc-200 transition-colors">{user?.name}</p>
                <p className="text-xs text-indigo-600 dark:text-zinc-400 font-medium capitalize">
                  {user?.is_founder ? 'Founder' : user?.subscription_plan === 'unpaid' ? 'No Plan' : user?.subscription_plan === 'trial' ? 'Free Trial' : user?.subscription_plan === 'expired_trial' ? 'Trial Expired' : `${user?.subscription_plan || 'Basic'} Plan`}
                </p>
                <p className="text-xs text-zinc-400 dark:text-zinc-600 mt-0.5">Edit Profile</p>
              </div>
            </Link>
            <Button
              variant="ghost"
              onClick={handleLogout}
              className="w-full justify-start text-zinc-600 dark:text-zinc-500 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-500/10"
              data-testid="logout-btn"
            >
              <LogOut className="w-5 h-5 mr-3" />
              Sign Out
            </Button>
          </div>
        </div>
      </aside>
    </>
  );
};

export default function AppLayout({ children }) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const location = useLocation();
  const { user } = useAuth();
  const isFounder = !!user?.is_founder;

  // Bottom-nav items shown ALWAYS on mobile (no hamburger needed for these)
  // Founders see "Users" next to Swipe instead of "CVs" (CVs still in hamburger).
  const bottomNavItems = isFounder
    ? [
        { path: '/dashboard',     label: 'Home',   icon: <LayoutDashboard className="w-5 h-5" /> },
        { path: '/jobs',          label: 'Jobs',   icon: <Briefcase className="w-5 h-5" /> },
        { path: '/swipe',         label: 'Swipe',  icon: <Flame className="w-5 h-5" /> },
        { path: '/founder/users', label: 'Users',  icon: <Users className="w-5 h-5" /> },
        { path: '/applications',  label: 'Apps',   icon: <FolderOpen className="w-5 h-5" /> },
      ]
    : [
        { path: '/dashboard',    label: 'Home',  icon: <LayoutDashboard className="w-5 h-5" /> },
        { path: '/jobs',         label: 'Jobs',  icon: <Briefcase className="w-5 h-5" /> },
        { path: '/swipe',        label: 'Swipe', icon: <Flame className="w-5 h-5" /> },
        { path: '/applications', label: 'Apps',  icon: <FolderOpen className="w-5 h-5" /> },
        { path: '/cv',           label: 'CVs',   icon: <FileText className="w-5 h-5" /> },
      ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-zinc-50 to-white dark:from-[#0a0a0a] dark:to-[#0e0e0e]">
      <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />
      
      <div className="lg:ml-64">
        {/* Mobile header */}
        <div className="sticky top-0 z-30 bg-[#111111]/90 backdrop-blur-md border-b border-white/[0.08] px-4 py-2.5 lg:hidden flex items-center gap-3">
          <button onClick={() => setSidebarOpen(true)} className="text-zinc-300 hover:text-white p-1" data-testid="mobile-menu-btn" aria-label="More options">
            <Menu className="w-7 h-7" />
          </button>
          <Link to="/" className="flex items-center -ml-1">
            <img src="https://static.prod-images.emergentagent.com/jobs/4621c343-adba-42bf-84be-49e31a6afafb/images/40c8a552cca65f4dba8d2de4cceb45ed6fa5e8f8b68ecd07be2fab8cf5594a0e.png" alt="JobSurf AI" className="h-[48px] w-[48px] object-contain rounded-lg brightness-110 contrast-110" />
          </Link>
        </div>

        {/* Extra bottom padding on mobile so the fixed bottom nav doesn't cover content */}
        <main className="p-4 sm:p-6 lg:p-8 pb-24 lg:pb-8">
          <EmailVerificationBanner />
          {children}
        </main>
      </div>

      {/* First-run CV upload popup — fires once per session if the user has no CV */}
      <FirstRunCVModal />

      {/* Persistent mobile bottom nav (hidden on lg+) */}
      <nav
        className="fixed bottom-0 inset-x-0 z-40 lg:hidden bg-[#0f0f0f]/95 backdrop-blur-md border-t border-white/[0.08] pb-[env(safe-area-inset-bottom)]"
        data-testid="mobile-bottom-nav"
      >
        <div className="grid grid-cols-5">
          {bottomNavItems.map((item) => {
            const isActive = location.pathname === item.path || location.pathname.startsWith(item.path + '/');
            return (
              <Link
                key={item.path}
                to={item.path}
                onMouseEnter={() => prefetchRoute(item.path)}
                className={`flex flex-col items-center justify-center gap-0.5 py-2.5 transition-colors active:scale-95 ${
                  isActive
                    ? 'text-amber-400'
                    : 'text-zinc-400 hover:text-white'
                }`}
                data-testid={`bottom-nav-${item.label.toLowerCase()}`}
              >
                {item.icon}
                <span className="text-[10px] font-medium leading-tight">{item.label}</span>
                {isActive && <span className="absolute top-0 w-8 h-0.5 bg-amber-400 rounded-full" />}
              </Link>
            );
          })}
        </div>
      </nav>
    </div>
  );
}

export { Sidebar };
